import { Form, Formik } from 'formik';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useRef, useState } from 'react';
import EmailEditor, { EmailEditorProps } from 'react-email-editor';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { TEXT_BUTTON_HEADER_LIST } from '../../../config/config';
import { PermissionColor } from '../../../permissions/PermissionColor';
import colorStore from '../../../stores/colorStore';
import newsletterStore from '../../../stores/newsletterStore';
import BreadCrumbComponent from '../../atoms/BreadCrumbComponent';
import ActionButton from '../../atoms/ButtonComponent';
import InputComponet from '../../atoms/InputComponet';
import PermissionComponent from '../../atoms/PermissionComponent';
import ContentBox from '../../molecules/ContentBox';
import LoadingComponent from '../../molecules/LoadingComponent';
import LoadingWrapper from '../../molecules/LoadingWrapper';
import Permission from '../../molecules/Permission';
import { useAuth } from '../../protected/ProviderAuth';
import { validationSchema } from './schema';
import './styles.scss';

const breadCrumb = [
    {
        title: <Link to="/dashboard">Dashboard</Link>,
    },
    {
        title: <Link to="/newsletter">Listagem de Newsletter</Link>,
    },
    {
        title: <p>Cadastro de Newsletter</p>,
    },
]

const CreateNewsletter: React.FC = observer(() => {
    const { uuid } = useParams<{ uuid: string }>();
    const { newsletter, loading } = newsletterStore;
    const navigate = useNavigate();
    const { hasPermission } = useAuth();
    const emailEditorRef = useRef<any>(null);
    const [loadingNewsletter, setLoadingNewsletter] = useState(true);

    useEffect(() => {
        const getInformation = async () => {
            if (uuid && hasPermission(PermissionColor.GET_UUID)) {
                newsletterStore.getNewsletterByUuid(uuid);
            }
        };

        getInformation();

        return () => {
            newsletterStore.reset();
        };
    }, [uuid]);

    const initialValues: any = {
        name: newsletter?.name || '',
        emailTitle: newsletter?.emailTitle || '',
        uuid: newsletter?.uuid || undefined,
    };

    // Faz o filtro da tabela
    const handleSubmit = async (values: any) => {
        let response;

        const unlayer = emailEditorRef.current?.editor;
        unlayer?.exportHtml(async (data: any) => {
            const { design, html } = data;

            const createProps = {
                ...values,
                active: true,
                deleted: false,
                emailBody: html,
                design: JSON.stringify(design),
            }

            if (uuid) {
                response = await newsletterStore.editNewsletter(createProps, uuid);
            } else {
                response = await newsletterStore.createNewsletter(createProps);
            }

            if (!response.error) listRouter();
        });
    };


    const listRouter = () => {
        navigate(`/newsletter`);
    }

    const onReady: EmailEditorProps['onReady'] = (unlayer) => {
        //@ts-ignore
        unlayer.exportHtml((html: string, css: string) => {
            setLoadingNewsletter(false);
        });
    };


    const onLoad = () => {
        if (uuid) {
            if (newsletter?.design && emailEditorRef) {
                const templateJson = JSON.parse(newsletter.design);
                return emailEditorRef.current?.editor?.loadDesign(templateJson);
            } else {
                return emailEditorRef.current.editor?.loadDesign({});
            }
        }
    };

    return (
        <>
            <BreadCrumbComponent breadCrumbList={breadCrumb} permission={PermissionColor.GET} textButton={TEXT_BUTTON_HEADER_LIST} onClick={() => listRouter()} />

            <LoadingWrapper loading={loading} title="Cadastro de Newsletter" description="Tela para cadastro de Newsletter">
                <Permission
                    permissionKeys={[PermissionColor.POST, PermissionColor.PUT, PermissionColor.GET_UUID]}
                    fallback={<PermissionComponent />}
                >
                    <Formik
                        initialValues={initialValues}
                        onSubmit={handleSubmit}
                        validationSchema={validationSchema}
                    >
                        <Form>
                            <ContentBox className="mb-4 p-6" title="Cadastro de Newsletter" description='Tela para cadastro de Newsletter'>
                                <div className="flex w-full gap-4 pb-4">
                                    <InputComponet label="Nome" name="name" id="name" />
                                    <InputComponet label="Titulo" name="emailTitle" id="emailTitle" />
                                </div>
                            </ContentBox>

                            <ContentBox className="mb-4 p-6" description='Editor de Template de E-mail'>

                                {loadingNewsletter && <LoadingComponent />}

                                <div className="newsletter-container">
                                    <EmailEditor
                                        ref={emailEditorRef}
                                        onReady={onReady}
                                        onLoad={onLoad}
                                    />
                                </div>
                            </ContentBox>

                            <div className="flex justify-end">
                                <Permission permissionKeys={[PermissionColor.PUT, PermissionColor.POST]} buttonType>
                                    <ActionButton htmlType="submit" actionType="primary">{uuid ? 'Atualizar' : 'Cadastrar'}</ActionButton>
                                </Permission>
                            </div>
                        </Form>
                    </Formik >
                </Permission>
            </LoadingWrapper>
        </>
    );
});

export default CreateNewsletter;